/**
 * Shown while the tracker's texts are being read for a range the formula
 * needs — never a partial ranking that would look complete. Binom answers
 * 1,000 texts per page, so a long range takes a minute or two the first
 * time; each range then stays for an hour and formula edits are instant.
 * For Media Devoted | Created By Ciro Autiero
 */
import { matchTracker, trackerFromEnum, trackerLabel, type TrackerEnum } from '@trafficpulse/shared';
import { FC } from 'react';
import { Loader2 } from 'lucide-react';
import { type DropGenTextReadingRange, hourRangeLabel } from '@trafficpulse/shared';

const fmt = (n: number) => n.toLocaleString('en-US');

const share = (r: DropGenTextReadingRange): number | null =>
  r.visitsTotal && r.visitsTotal > 0 ? Math.min(100, Math.round((r.visitsRead / r.visitsTotal) * 100)) : null;

export const ReadingPanel: FC<{ reading: DropGenTextReadingRange[]; provider: TrackerEnum }> = ({ reading, provider }) => (
  <div className="flex flex-col gap-3 rounded-lg border border-border px-4 py-4" role="status" aria-live="polite">
    <p className="flex items-center gap-2 text-sm font-medium">
      <Loader2 className="h-4 w-4 animate-spin text-primary motion-reduce:animate-none" />
      Reading every text of this campaign group from {trackerLabel(trackerFromEnum(provider))}…
    </p>
    <p className="max-w-3xl text-xs text-muted-foreground">
      {matchTracker(trackerFromEnum(provider), { voluum: () => '', 'tres-commas': () => '', binom: () => 'Binom answers 1,000 texts per page, so the first read of a long range takes a minute or two. ' })}
      The ranking appears when every text is in — never half a list. The read is then shared by every server for 12 hours and refreshed every 3 hours in the background, so this wait happens once, not on every open.
    </p>
    {reading.map((r) => {
      const pct = share(r);
      return (
        <div key={r.rangeKey} className="flex flex-col gap-1">
          <div className="flex flex-wrap items-baseline gap-x-2 text-xs">
            <span className="font-medium">{hourRangeLabel(r.rangeKey)}</span>
            <span className="text-muted-foreground">
              {r.pages > 0 ? `page ${r.pages} · ${fmt(r.texts)} texts read` : 'starting…'}
              {pct !== null ? ` · ${pct} % of the ${fmt(r.visitsTotal ?? 0)} visits` : ''}
            </span>
          </div>
          <span className="h-1.5 overflow-hidden rounded-full bg-muted">
            <span className={pct === null ? 'block h-full w-1/3 animate-pulse rounded-full bg-primary/50' : 'block h-full rounded-full bg-primary transition-[width] duration-500'} style={pct === null ? undefined : { width: `${pct}%` }} />
          </span>
        </div>
      );
    })}
  </div>
);
